import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api, can, codeWords, daysUntil, saveFile, shortDate, tierLabel, type Assessment, type Me, type Vendor, type VendorQuery, readIf } from '../lib/api'
import { questionnaireWord, statusWord, vendorStage, type StatusWord } from '../lib/status'
import { Chip } from '../components/StatusChip'
import { BandChip } from '../components/BandChip'
import { reasonWords } from '../lib/bandLine'
import '../styles/band-line.css'
import { DataTable, EmptyBlock, ErrorBox, ListCount, type Column, type Empty, type EmptyAction } from '../components/DataTable'
import { Avatar, Skeleton } from '../components/ui'
import { FilterBar, type FilterGroup } from '../components/FilterBar'
import { Status } from '../components/kit'
import '../styles/registers.css'
import { onScreen } from '../lib/screens'
import { addedInAddress, justAdded } from '../lib/justAdded'
import { registerNextStep } from '../lib/requestRules'
import { keepPanelY } from '../lib/tablePlace'
import {
  bandsAsked,
  bandSortValue,
  DEFAULT_SORT,
  emptyState,
  exportQuery,
  firstPage,
  GRID_BANDS,
  gridCellQuery,
  inCell,
  isFiltered,
  liveVendors,
  NO_FILTER,
  pageOneQuery,
  readFilter,
  readPlace,
  registerQuery,
  registerRows,
  serverQuery,
  type RegisterFilter,
  type RegisterPlace,
} from '../lib/registerView'

// The register: one list, two views (release 3.3, V3 of the 7 Oct plan,
// section 3.2). The table shows 25 rows a page in a scroll panel whose header
// row stays in view; the tier by risk band grid is the second view
// (?view=grid), where it had been a fold above the table. The page, the row
// size, the sort, the view and the filters are in the address, which they
// replace (Back model B5), so Back from a vendor lands on the same page, sorted
// and filtered the same, at the same row (B6). The decisions are plain
// functions in lib/registerView.ts.
//
// The vendor's stage and its questionnaire are two labelled columns in the
// vocabulary's words (lib/status.ts), because a "Draft" vendor beside an
// "Overdue" questionnaire read as one contradictory status (walk-through,
// 4 Oct 2026). Rows carry no task button: a row opens its vendor, where the one
// next step is (release 3.2). The Excel export is the Portfolio's call, asked
// for the rows on screen.

const BAND_LABEL: Record<string, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
  INSUFFICIENT_EVIDENCE: 'Insufficient evidence',
  NONE: 'Not scored',
}
// What the one filter control offers here. Tier is single choice in effect:
// the grid links carry one tier, and the control keeps the last picked.
const STATUSES = ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'OFFBOARDING', 'OFFBOARDED', 'REJECTED'] as const
const DATA = ['PERSONAL', 'SENSITIVE_PERSONAL', 'FINANCIAL', 'HEALTH'] as const

const BASE_FILTERS: FilterGroup[] = [
  { key: 'tier', label: 'Tier', options: [4, 3, 2, 1].map((t) => ({ value: String(t), label: tierLabel(t) })) },
  { key: 'band', label: 'Risk band', options: GRID_BANDS.map((b) => ({ value: b, label: BAND_LABEL[b] ?? b })) },
  { key: 'status', label: 'Stage', options: STATUSES.map((s) => ({ value: s, label: statusWord('vendor', s).word })) },
  { key: 'data', label: 'Data they hold', options: DATA.map((d) => ({ value: d, label: codeWords(d) })) },
]

// A questionnaire sent back for more answers is with the vendor again, so it
// is out and can be late exactly like one never answered.
const OPEN = new Set(['ISSUED', 'IN_PROGRESS', 'RETURNED'])
const REVIEW = new Set(['SUBMITTED', 'UNDER_REVIEW'])

// The columns a sort in the address may name.
const SORT_KEYS = ['vendor', 'tier', 'band', 'status', 'state', 'due'] as const

function name(v: Vendor): string {
  return v.displayName?.trim() || v.legalName
}

// The query part of the address (after the ?).
const query = () => window.location.hash.split('?')[1] ?? ''

export function Vendors({ me }: { me?: Me | null }) {
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [assessments, setAssessments] = useState<Assessment[]>([])
  // Whether the questionnaires were read at all: a reader without
  // assessment.read sees no questionnaire, which says nothing about whether
  // one was sent.
  const assessmentsRead = can('assessment.read')
  const sortKeys = assessmentsRead ? SORT_KEYS : SORT_KEYS.filter((k) => k !== 'state')
  const [error, setError] = useState<unknown>(null)
  const [filter, setFilter] = useState(() => readFilter(query()))
  const [place, setPlace] = useState(() => readPlace(query(), sortKeys))
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<ApiError | null>(null)
  // The vendors this person just added (release 3.3): pinned at the top and
  // marked New for the session, and named once in the confirmation above the
  // list until it is closed. The address carries the ones from this landing.
  const [addedNow, setAddedNow] = useState(() => addedInAddress())
  const pinnedIds = useMemo(() => justAdded(addedNow), [addedNow])
  // The one search box in the header bar narrows this list. A new search is
  // a new list: its first page, at the top of the panel (review of V3, S3).
  const [search, setSearch] = useState('')
  const searched = useRef('')
  useEffect(() => {
    const onQuery = (e: Event) => {
      const next = (e as CustomEvent<string>).detail ?? ''
      if (next === searched.current) return
      searched.current = next
      setSearch(next)
      keepPanelY(0)
      const moved = pageOneQuery(query(), sortKeys, addedInAddress())
      if (moved != null) {
        history.replaceState(history.state, '', '#/vendors' + (moved ? '?' + moved : ''))
        setPlace(readPlace(query(), sortKeys))
      }
    }
    window.addEventListener('tprm:query', onQuery)
    return () => window.removeEventListener('tprm:query', onQuery)
    // sortKeys follows the reader's capabilities, fixed for the visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const sync = () => {
      setFilter(readFilter(query()))
      setPlace(readPlace(query(), sortKeys))
      setAddedNow(addedInAddress())
    }
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
    // sortKeys follows the reader's capabilities, fixed for the visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const askedKey = JSON.stringify(serverQuery(filter))
  const narrowed = askedKey !== '{}'

  useEffect(() => {
    let live = true
    setVendors(null)
    Promise.all([api.vendors(JSON.parse(askedKey) as VendorQuery), readIf('assessment.read', () => api.assessments(), { assessments: [] as Assessment[] }).catch(() => ({ assessments: [] as Assessment[] }))])
      .then(([p, a]) => {
        if (!live) return
        setVendors(p.vendors ?? [])
        setAssessments(a.assessments ?? [])
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [askedKey])

  const stateByVendor = useMemo(() => {
    const m = new Map<string, VendorQ>()
    for (const a of assessments) {
      const cur = m.get(a.vendorId) ?? { any: false }
      cur.any = true
      if (OPEN.has(a.status)) cur.open = a
      if (REVIEW.has(a.status)) cur.review = a
      if (!cur.latest || (a.submittedAt ?? a.issuedAt ?? '') > (cur.latest.submittedAt ?? cur.latest.issuedAt ?? '')) cur.latest = a
      m.set(a.vendorId, cur)
    }
    return m
  }, [assessments])

  if (error != null) return <ErrorBox error={error} what="The register" />
  if (!vendors)
    return (
      <div className="p1-page">
        <h1>Vendors</h1>
        <Skeleton lines={6} height={18} />
      </div>
    )

  // Offboarded and rejected vendors leave the register, unless somebody asked
  // for a status, in which case the API has already chosen the rows.
  const live = liveVendors(vendors, filter)
  const rows = registerRows(vendors, filter, search)

  const columns: Column<Vendor>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (v) => name(v).toLowerCase(),
      render: (v) => (
        // A floor under the name: on a laptop-width window the column was
        // squeezed to a word per line, and the link was a 40 pixel target.
        <span style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 200 }}>
          <Avatar name={name(v)} size={30} />
          <span>
            <a href={'#/vendor/' + v.id}>
              <strong>{name(v)}</strong>
            </a>{' '}
            {pinnedIds.has(v.id) && <Status tone="brand">New</Status>}
            <div className="cell-sub mono">{v.vendorCode}</div>
          </span>
        </span>
      ),
    },
    { key: 'tier', header: 'Tier', sortValue: (v) => v.inherentTier ?? 9, render: (v) => (v.inherentTier ? tierLabel(v.inherentTier) : <span className="muted">not set</span>) },
    {
      key: 'band',
      header: 'Risk',
      // Riskiest first by rank, then by name, as the export orders its rows
      // (review of V3, S4; lib/registerView.ts, bandSortValue).
      sortValue: (v) => bandSortValue(v),
      // The band and, under it, the one line that says why (release 3.3, N2),
      // cut at two lines with the whole line on hover and to a screen reader.
      render: (v) => <BandCell v={v} />,
    },
    {
      key: 'status',
      header: 'Stage',
      sortValue: (v) => v.status,
      // A requested vendor's next step is said under its stage (release 3.3,
      // C4 of the 12 Oct plan): it waits on the TPRM lead, and the row is
      // where somebody scanning the register looks for whose turn it is.
      render: (v) => {
        const next = registerNextStep(v)
        return (
          <span className="reg-q">
            <Chip s={vendorStage(v.status, assessmentsRead ? (stateByVendor.get(v.id)?.any ?? false) : null, v.requestApproved)} />
            {next && <span className="cell-sub">{next}</span>}
          </span>
        )
      },
    },
    // Only for a reader who may read questionnaires: without assessment.read
    // none was read, and every vendor would say Not sent.
    ...(assessmentsRead
      ? [
          {
            key: 'state',
            header: 'Questionnaire',
            sortValue: (v: Vendor) => stateOf(v, stateByVendor).s.word,
            render: (v: Vendor) => {
              const st = stateOf(v, stateByVendor)
              return (
                <span className="reg-q">
                  <Chip s={st.s} />
                  {st.line && <span className={'cell-sub' + (st.late ? ' cell-late' : '')}>{st.line}</span>}
                </span>
              )
            },
          },
        ]
      : []),
    { key: 'due', header: 'Next review', sortValue: (v) => v.nextDueOn ?? '9999', render: (v) => (v.nextDueOn ? <span className="mono">{shortDate(v.nextDueOn)}</span> : <span className="muted">none</span>) },
  ]

  // A filter, a page, a sort or a view replaces the address rather than
  // adding a history entry, as on every other list (Back model B5): Back
  // leaves the register, not the last page. replaceState raises no event, so
  // the list reads it here.
  const setHash = (q: string) => {
    history.replaceState(history.state, '', '#/vendors' + (q ? '?' + q : ''))
    setFilter(readFilter(query()))
    setPlace(readPlace(query(), sortKeys))
    setAddedNow(addedInAddress())
  }
  // Every change here is a new page, list or order, so the panel's saved
  // place is the top: only Back to this entry from a vendor puts a row back
  // (review of V3, S3).
  const go = (next: { filter?: RegisterFilter; place?: RegisterPlace; added?: string[] }) => {
    keepPanelY(0)
    setHash(registerQuery(next.filter ?? filter, next.place ?? place, next.added ?? addedNow))
  }

  // The rows on screen, as a workbook: the Portfolio's export, asked for what
  // this list shows (lib/registerView.ts, exportQuery).
  function exportToExcel(): void {
    setExporting(true)
    setExportError(null)
    api
      .portfolioExport(exportQuery(filter, search))
      .then(saveFile)
      .catch((err) => setExportError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setExporting(false))
  }

  // The owner and the two flags have one value each, so they are offered only
  // while they are applied: as a chip that says what the list is narrowed to.
  const ownerLabel = me && filter.owner === me.subject ? 'Vendors you own' : 'Owned by ' + filter.owner
  const other = [...(filter.owner ? ['owner'] : []), ...(filter.crossBorder ? ['crossBorder'] : []), ...(filter.overdue ? ['overdue'] : [])]
  const filters: FilterGroup[] = [
    ...BASE_FILTERS,
    ...(other.length > 0
      ? [
          {
            key: 'other',
            label: 'Also narrowed to',
            options: [
              ...(filter.owner ? [{ value: 'owner', label: ownerLabel }] : []),
              ...(filter.crossBorder ? [{ value: 'crossBorder', label: 'Data outside India' }] : []),
              ...(filter.overdue ? [{ value: 'overdue', label: 'Reassessment overdue' }] : []),
            ],
          },
        ]
      : []),
  ]

  // Why the list is empty and the one way out (lib/registerView.ts).
  const why = emptyState({
    read: vendors.length,
    filtered: isFiltered(filter),
    searched: search.trim() !== '',
    canImport: can('vendor.import') && onScreen('import'),
    canAdd: can('vendor.create') && onScreen('vendor-add'),
  })
  const ways: EmptyAction[] = why.actions.map((a) =>
    a === 'import'
      ? { label: 'Import vendors', href: '#/import' }
      : a === 'add'
        ? { label: 'Add a vendor', href: '#/vendor/new' }
        : { label: 'Clear filters', onClick: () => go({ filter: NO_FILTER, place: firstPage(place) }) },
  )
  const empty: Empty = { title: why.title, detail: why.detail, action: ways[0], also: ways[1] }
  const grid = place.view === 'grid'

  return (
    <div className="p1-page">
      <div className="p1-head">
        <div>
          <h1>Vendors</h1>
        </div>
        {/* Bringing a register in is a workflow on this list, like adding one
            vendor, rather than a menu item beside it (release 3.2). */}
        {can('vendor.import') && onScreen('import') && (
          <div className="p1-actions">
            <a className="button" href="#/import">
              Import vendors
            </a>
          </div>
        )}
      </div>

      {addedNow.length > 0 && (
        <AddedNote
          vendors={addedNow.map((id) => vendors.find((v) => v.id === id)).filter((v): v is Vendor => v != null)}
          onClose={() => go({ added: [] })}
        />
      )}

      <FilterBar
        groups={filters}
        value={{
          tier: filter.tier == null ? [] : [String(filter.tier)],
          band: bandsAsked(filter),
          status: filter.status,
          data: filter.data,
          other,
        }}
        onChange={(v) => {
          const kept = v.other ?? []
          const tiers = v.tier ?? []
          go({
            filter: {
              tier: tiers.length > 0 ? Number(tiers[tiers.length - 1]) : null,
              band: (v.band ?? []).filter((b) => b !== 'NONE'),
              unscored: (v.band ?? []).includes('NONE'),
              status: v.status ?? [],
              data: v.data ?? [],
              owner: kept.includes('owner') ? filter.owner : '',
              crossBorder: kept.includes('crossBorder'),
              overdue: kept.includes('overdue'),
            },
            place: firstPage(place),
          })
        }}
      >
        {/* One list, two views (plan 3.2): the choice is in the address. */}
        <span className="reg-seg" role="group" aria-label="Show the vendors as">
          <button type="button" aria-pressed={!grid} onClick={() => go({ place: { ...place, view: 'table' } })}>
            Table
          </button>
          <button type="button" aria-pressed={grid} onClick={() => go({ place: { ...place, view: 'grid' } })}>
            Risk grid
          </button>
        </span>
        {/* The Portfolio's export, offered to whoever the Portfolio is. */}
        {onScreen('portfolio') && rows.length > 0 && (
          <button type="button" data-action="tprm.portfolio.a02" onClick={exportToExcel} disabled={exporting}>
            {exporting ? 'Exporting' : 'Export to Excel'}
          </button>
        )}
      </FilterBar>

      {exportError && (
        <div className="error-box" role="alert">
          <h3>The export did not download ({exportError.code})</h3>
          <p>{exportError.message}</p>
        </div>
      )}

      {grid ? (
        rows.length === 0 ? (
          <EmptyBlock empty={empty} />
        ) : (
          // The same list as the table, with the same counts (review of V3,
          // S2): the chips and the search narrow the grid too, and Export
          // sends exactly these vendors.
          <RiskGrid
            rows={rows}
            narrowed={isFiltered(filter) || search.trim() !== ''}
            href={(t, b) => '#/vendors?' + gridCellQuery(filter, place, t, b)}
            onPick={(t, b) => setHash(gridCellQuery(filter, place, t, b))}
          />
        )
      ) : (
        <DataTable
          caption="Vendors"
          columns={columns}
          rows={rows}
          rowKey={(v) => v.id}
          defaultSort={DEFAULT_SORT}
          sort={place.sort}
          onSort={(sort) => go({ place: { ...firstPage(place), sort } })}
          paging={{
            label: 'Vendor rows',
            scope: registerQuery(filter, firstPage(place)) + '|' + search.trim(),
            page: place.page,
            size: place.size,
            onPage: (page) => go({ place: { ...place, page } }),
            onSize: (size) => go({ place: { ...firstPage(place), size } }),
          }}
          rowHref={(v) => '#/vendor/' + v.id}
          pinned={(v) => pinnedIds.has(v.id)}
          pinnedLabel="Just added"
          empty={empty}
          // The pager says "1-25 of 142" of what is listed; the count says
          // what the tier, band and search held back from the register read.
          // Narrowed by the address, the read holds only what matched, so
          // there is no larger whole to be a part of.
          footer={!narrowed && rows.length !== live.length ? <ListCount shown={rows.length} total={live.length} noun="vendors" /> : null}
        />
      )}
    </div>
  )
}

// The second view: the same vendors as the table, by tier and risk band. A
// number opens the table narrowed to that cell, in place (the address is
// replaced, not pushed); a modified press is the browser's (a new tab).
function RiskGrid({
  rows,
  narrowed,
  href,
  onPick,
}: {
  rows: Vendor[]
  narrowed: boolean
  href: (tier: number, band: string) => string
  onPick: (tier: number, band: string) => void
}) {
  const total = (band: string) => rows.filter((v) => (v.currentBand ?? 'NONE') === band).length
  return (
    <div className="reg-grid">
      <table className="p1-grid" aria-label="Vendors by tier and risk band">
        <thead>
          <tr>
            <th className="row">Tier</th>
            {GRID_BANDS.map((b) => (
              <th key={b}>{BAND_LABEL[b]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4].map((t) => (
            <tr key={t}>
              <th className="row">{tierLabel(t)}</th>
              {GRID_BANDS.map((b) => {
                const n = rows.filter((v) => inCell(v, t, b)).length
                const hot = n > 0 && (b === 'CRITICAL' || (b === 'HIGH' && t <= 2))
                const warm = n > 0 && !hot && (b === 'HIGH' || b === 'INSUFFICIENT_EVIDENCE')
                return (
                  <td key={b} className={n === 0 ? 'zero' : hot ? 'hot' : warm ? 'warm' : ''}>
                    {n === 0 ? (
                      '0'
                    ) : (
                      <a
                        href={href(t, b)}
                        onClick={(e) => {
                          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return
                          e.preventDefault()
                          onPick(t, b)
                        }}
                      >
                        {n}
                      </a>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
          <tr>
            <th className="row">All</th>
            {GRID_BANDS.map((b) => (
              <td key={b}>{total(b)}</td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className="muted reg-grid-note">
        {narrowed ? 'Counts the vendors the filters and the search leave. ' : ''}A number opens the table with those vendors.
      </p>
    </div>
  )
}

// The confirmation after adding: what was added and the one way into it. It
// stays until closed, never takes the focus, and is read out once (WCAG 4.1.3).
export function AddedNote({ vendors, onClose }: { vendors: Vendor[]; onClose: () => void }) {
  if (vendors.length === 0) return null
  const one = vendors.length === 1 ? vendors[0] : null
  return (
    <div className="note ok reg-added" role="status">
      <p>
        {one ? (
          <>
            <strong>Vendor added: {name(one)}.</strong> <a href={'#/vendor/' + one.id}>Open vendor</a>
          </>
        ) : (
          <>
            <strong>{vendors.length} vendors added.</strong> They are at the top of the list.
          </>
        )}
      </p>
      <button type="button" className="ghost" onClick={onClose} aria-label="Close the confirmation">
        Close
      </button>
    </div>
  )
}

type VendorQ = { any: boolean; open?: Assessment; review?: Assessment; latest?: Assessment }

// The vendor's questionnaire as a chip in the vocabulary's words and one short
// line: the due date, or how late.
function stateOf(v: Vendor, m: Map<string, VendorQ>): { s: StatusWord; line: string; late: boolean } {
  const q = m.get(v.id)
  if (q?.review) return { s: statusWord('questionnaire', q.review.status), line: q.review.reference, late: false }
  if (q?.open) {
    const d = daysUntil(q.open.dueOn)
    const late = d != null && d < 0
    const line = late ? Math.abs(d) + (Math.abs(d) === 1 ? ' day late' : ' days late') : q.open.dueOn ? 'due ' + shortDate(q.open.dueOn) : ''
    return { s: questionnaireWord(q.open.status, late), line, late }
  }
  if (q?.latest) return { s: statusWord('questionnaire', q.latest.status), line: q.latest.reference, late: false }
  if (v.currentBand) return { s: statusWord('questionnaire', 'SCORED'), line: '', late: false }
  return { s: statusWord('questionnaire', 'NOT_SENT'), line: '', late: false }
}

// The register's risk cell: the band, and under it the one line that says why
// (release 3.3, N2), cut at two lines with the whole line on hover.
export function BandCell({ v }: { v: Vendor }) {
  if (!v.currentBand) return <span className="muted">not scored</span>
  const why = reasonWords(v.bandReason)
  return (
    <span className="band-cell">
      <BandChip band={v.currentBand} />
      {why && (
        <span className="band-why" title={why}>
          {why}
        </span>
      )}
    </span>
  )
}
